// MassiveMessageModal.tsx
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Typography, Snackbar, Alert, FormControl, InputLabel, MenuItem, Select, Box } from "@mui/material";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { LoadState } from "../../redux/common";
import { MessagePriority, getMessagePriorityColor, getMessagePriorityName } from "../../redux/conversation/messagePriority";
import { getMessageTypeName, MessageType } from "../../redux/conversation/messageListSlice";
import ImageUploadDragAndDrop from "../base/image/ImageUploadDragAndDrop";
import FileUploadDragAndDrop from "../base/file/FileUploadDragAndDrop";
import { useAppDispatch } from "../../app/hooks";
import { ObjectTarget } from "../../app/permissions";
import { ObjectTargetId, performMessageSendMassive } from "../../redux/conversation/messageSendMassiveSlice";

interface MassiveMessageModalProps {
    objectName: string;
    open: boolean;
    target: ObjectTarget;
    targetId?: string | number;
    onClose: (success: boolean) => void;
}

const MassiveMessageModal = (props: MassiveMessageModalProps) => {
    const { objectName, open } = props;

    const dispatch = useAppDispatch();

    const [message, setMessage] = useState("");
    const [showError, setShowError] = useState(false);
    const [priority, setPriority] = useState<MessagePriority>(MessagePriority.Regular);
    const [messageType, setMessageType] = useState<MessageType>(MessageType.TEXT);
    const [filename, setFilename] = useState<string | undefined>(undefined);

    const messageSendMassive = useSelector((state: any) => state.messageSendMassive);

    function onSend() {
        console.log("clickMassiveMessage");
        const target = {
            objectTargetId: `${props.target}`,
            targetId: `${props.targetId}`,
        };
        if (target) {
            const targets = [target] as ObjectTargetId[];
            dispatch(performMessageSendMassive({
                targets: targets,
                text: message,
                messagePriority: priority,
                messageType: messageType,
                filename: filename,
            }));
        }
    }

    function onClose(success: boolean) {
        setMessage("");
        setShowError(false);
        setPriority(MessagePriority.Regular);
        setMessageType(MessageType.TEXT);
        setFilename(undefined);
        props.onClose(success);
    }

    useEffect(() => {
        if (messageSendMassive.state.state === LoadState.Loaded) {
            setMessage("");
            setMessageType(MessageType.TEXT);
            onClose(true);
        }
        if (messageSendMassive.state.state === LoadState.Failed) {
            setShowError(true);
        }
    }, [messageSendMassive]);

    const sending = messageSendMassive.state.state === LoadState.Loading;

    return (
        <>
            <Dialog open={open} onClose={() => onClose(false)} fullWidth maxWidth="sm">
                <DialogTitle>Enviar mensagem em massa</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" gutterBottom>
                        Esta mensagem será enviada para todos os contatos associados à <b>{objectName}</b> em sua <b>conversa privada</b>.
                    </Typography>

                    {elMessageType()}
                    
                    {elText()} {elImage()} {elPDF()}
                    
                    {elPriority()}
                    {elPriorityMessage()}

                </DialogContent>
                <DialogActions>
                    <Button onClick={() => onClose(false)}
                        disabled={sending} variant="outlined">
                        Cancelar
                    </Button>
                    <Button onClick={onSend} variant="contained" disabled={message.trim() === "" || sending}>
                        Enviar
                    </Button>
                </DialogActions>
            </Dialog>
            <Snackbar open={showError} autoHideDuration={5000} onClose={() => setShowError(false)}>
                <Alert onClose={() => setShowError(false)} severity="error" sx={{ width: "100%" }}>
                    Erro ao enviar mensagem em massa.
                </Alert>
            </Snackbar>
        </>
    );

    function elMessageType() {
        return <FormControl fullWidth sx={{ mt: 1, mb: 2 }}>
            <InputLabel id="message-type-label">Tipo de Mensagem</InputLabel>
            <Select
                labelId="message-type-label"
                id="message-type-select"
                value={messageType}
                label="Tipo de Mensagem"
                disabled={sending}
                onChange={(e) => {
                    setMessage("");
                    setMessageType(Number(e.target.value) as MessageType);
                } }
            >
                <MenuItem value={MessageType.TEXT}>{getMessageTypeName(MessageType.TEXT)}</MenuItem>
                <MenuItem value={MessageType.IMAGE}>{getMessageTypeName(MessageType.IMAGE)}</MenuItem>
                <MenuItem value={MessageType.PDF}>{getMessageTypeName(MessageType.PDF)}</MenuItem>
            </Select>
        </FormControl>;
    }

    function elPriorityMessage() {
        return priority !== MessagePriority.Regular && (
            <Box sx={{
                p: 1, mb: 2, borderRadius: 1,
                backgroundColor: (theme) => getMessagePriorityColor(theme, priority).main,
                border: (theme) => getMessagePriorityColor(theme, priority).contrastText
            }}>
                <Typography sx={{
                    color: (theme) => getMessagePriorityColor(theme, priority).contrastText
                }}>
                    Esta mensagem será exibida na tela inicial e deverá ser marcada como lida pelos pais.
                </Typography>
            </Box>
        );
    }

    function elPriority() {
        return <FormControl fullWidth sx={{ mt: 1, mb: 2 }}>
            <InputLabel id="priority-label">Prioridade</InputLabel>
            <Select
                labelId="priority-label"
                id="priority-select"
                value={priority}
                label="Prioridade"
                onChange={(e) => setPriority(Number(e.target.value) as MessagePriority)}
            >
                <MenuItem value={MessagePriority.Regular}>{getMessagePriorityName(MessagePriority.Regular)}</MenuItem>
                <MenuItem value={MessagePriority.Success}>{getMessagePriorityName(MessagePriority.Success)}</MenuItem>
                <MenuItem value={MessagePriority.Info}>{getMessagePriorityName(MessagePriority.Info)}</MenuItem>
                <MenuItem value={MessagePriority.Warning}>{getMessagePriorityName(MessagePriority.Warning)}</MenuItem>
                <MenuItem value={MessagePriority.Error}>{getMessagePriorityName(MessagePriority.Error)}</MenuItem>
            </Select>
        </FormControl>;
    }

    function elPDF() {
        return messageType === MessageType.PDF &&
            <FileUploadDragAndDrop
                onChange={(file) => {
                    if (file) {
                        const reader = new FileReader();
                        reader.onloadend = () => {
                            const base64data = reader.result as string;
                            setMessage(base64data);
                        };
                        reader.readAsDataURL(file);
                        setFilename(file.name);
                    }
                } }
                filetype="PDF" />;
    }

    function elImage() {
        return messageType === MessageType.IMAGE &&
            <ImageUploadDragAndDrop
                value={message}
                onChange={setMessage}
                alt="Imagem da mensagem em massa" />;
    }

    function elText() {
        return messageType === MessageType.TEXT &&
            <TextField
                autoFocus
                margin="dense"
                id="massive-message"
                label="Mensagem"
                type="text"
                fullWidth
                multiline
                minRows={3}
                value={message}
                disabled={sending}
                onChange={e => setMessage(e.target.value)} />;
    }
};

export default MassiveMessageModal;